// Shared helpers for dashboard widgets. Extracted from
// DashboardPage.tsx during the Commit-0 refactor so each widget
// file owns its render but reaches into the same formatting +
// delta primitives.

import type { OperatorDashboardData } from '@evi/shared';

/** Delta badge values for `KpiCard.delta` — shape matches @evi/ui. */
export type TileDelta = { value: string; direction: 'up' | 'down' | 'flat' };

export function formatCurrency(v: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);
}

export function formatEnergy(kwh: number): string {
  if (kwh >= 1000) return `${(kwh / 1000).toFixed(1)} MWh`;
  return `${kwh.toFixed(1)} kWh`;
}

export function formatPctDelta(pct: number): TileDelta {
  if (!Number.isFinite(pct) || pct === 0) return { value: '—', direction: 'flat' };
  const rounded = Math.round(pct);
  return {
    value: `${rounded >= 0 ? '+' : ''}${rounded}%`,
    direction: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat',
  };
}

export function formatCountDelta(n: number): TileDelta {
  if (n === 0) return { value: '—', direction: 'flat' };
  return {
    value: `${n > 0 ? '+' : ''}${n}`,
    direction: n > 0 ? 'up' : 'down',
  };
}

export function chargerDelta(summary: OperatorDashboardData): TileDelta {
  const { online, total, faulted } = summary.network;
  const offline = total - online;
  if (faulted > 0) return { value: `${faulted} faulted`, direction: 'down' };
  if (offline === 0) return { value: 'All online', direction: 'up' };
  return { value: `-${offline} offline`, direction: 'down' };
}

/** Compact relative-time formatter for the Recent Sessions list.
 *  "5m ago" / "2h ago" / "May 3" — older than 24h falls back to a
 *  date string so the row doesn't spam "47h ago." */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 60_000) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** "May 8" / "Today" / "Tomorrow" — terse arrival copy for the
 *  payout subtitle. Falls through to the locale date for anything
 *  past tomorrow. */
export function formatPayoutDate(iso: string): string {
  const arrival = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const arrivalDay = new Date(arrival);
  arrivalDay.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((arrivalDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (dayDiff === 0) return 'today';
  if (dayDiff === 1) return 'tomorrow';
  return arrival.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatSettlementDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "12.3 hr" / "47 min" — terser than minutes for high values. */
export function formatActiveHours(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} hr`;
}
